import {
    Alert,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Booking,
    BookingCalendarSelection,
    BookingDeviceModalMode,
    BookingFormErrorMessage,
    Device,
    SlotIndex,
} from '../types';
import TextField from '@mui/material/TextField';
import { ChangeEvent, Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAllBookings, selectClosestBooking } from '../store/selectors';
import { useTranslation } from 'react-i18next';
import {
    containsRange,
    findClosestBooking,
    generateSlotsIndexes,
    generateSlotsIndexesRange,
    getBookingDateRangeFromDatesDifference,
    getBookingSelectionFromDaysAndHours,
    getNeighborBookingFirstSlotIndex,
    hasMinimumHourDuration,
    hasMinimumRequiredBookingDuration,
} from '@pages/booking/utils';
import {
    MAP_DEVICE_TYPE_TO_PORTS_PER_SLOT_COUNT,
    MAX_DAYS_BOOKING,
    MIN_DAYS_BOOKING,
    MIN_HOURS_BOOKING,
} from '@pages/booking/constants';
import { dateFormatting, enforceMinMax, toTitleCase } from '@shared/utils/utilsOld';
import { BookingState } from '@pages/booking/store';
import { getDatesDifferenceFromDateRange } from '@shared/utils/utils';
import { useBookingConfigContext } from '@pages/booking/contexts/BookingConfigContext';

interface BookingDeviceModalFormProps {
    mode: BookingDeviceModalMode;
    bookingForItem: Booking | null;
    daysCount: number;
    hoursCount: number;
    slotsCount: number;
    dateRangeErrorMessage: BookingFormErrorMessage;
    setDaysCount: Dispatch<SetStateAction<number>>;
    setHoursCount: Dispatch<SetStateAction<number>>;
    setSlotsCount: Dispatch<SetStateAction<number>>;
    setBookingsSelection: Dispatch<SetStateAction<BookingCalendarSelection>>;
    setDateRangeErrorMessage: Dispatch<SetStateAction<BookingFormErrorMessage>>;
    bookingStartDate: Date;
    isBookingInPast: boolean;
    futureBookingFrom?: string;
    futureBookingTo?: string;
    slotIndex?: SlotIndex;
    selectedDevice?: Device;
}

const BookingDeviceModalForm: FC<BookingDeviceModalFormProps> = ({
    mode,
    bookingForItem,
    daysCount,
    hoursCount,
    slotsCount,
    dateRangeErrorMessage,
    setDaysCount,
    setHoursCount,
    setSlotsCount,
    setBookingsSelection,
    setDateRangeErrorMessage,
    bookingStartDate,
    isBookingInPast,
    futureBookingFrom,
    futureBookingTo,
    slotIndex,
    selectedDevice,
}) => {
    const { t } = useTranslation();
    const { localBookingsStorageMode, bookingRequiredPeriod, minimumHourDuration } = useBookingConfigContext();
    const closestBooking = useSelector(selectClosestBooking);
    const bookings = useSelector((state: { booking: BookingState }) => selectAllBookings(state, selectedDevice?.pk));
    const [isInitialized, setIsInitialized] = useState(false);

    const closestBookingWithLocal = useMemo(() => {
        if (!localBookingsStorageMode || !selectedDevice) {
            return closestBooking;
        }

        const closestLocalBooking = findClosestBooking(bookingStartDate, bookings);

        if (closestLocalBooking && closestBooking) {
            return new Date(closestLocalBooking.booked_from).getTime() < new Date(closestBooking.booked_from).getTime()
                ? closestLocalBooking
                : closestBooking;
        }

        return closestLocalBooking ?? closestBooking;
    }, [bookingStartDate, bookings, closestBooking, localBookingsStorageMode, selectedDevice]);

    const makeSelectionAndValidation = useCallback(
        (actualDaysCount: number, actualHoursCount: number, actualSlotsCount?: number) => {
            function getFutureBookingSlots() {
                return slotIndex !== undefined && actualSlotsCount
                    ? generateSlotsIndexesRange(slotIndex, actualSlotsCount)
                    : undefined;
            }

            function makeSelection() {
                const slotsIndexes = getFutureBookingSlots();

                setBookingsSelection(
                    getBookingSelectionFromDaysAndHours(
                        actualDaysCount,
                        actualHoursCount,
                        bookingStartDate,
                        slotsIndexes,
                    ),
                );
            }

            function makeValidation() {
                const [selectedBookingFrom, selectedBookingTo] = getBookingDateRangeFromDatesDifference(
                    actualDaysCount,
                    actualHoursCount,
                    bookingStartDate,
                );
                const selectedBookingFromDate = new Date(selectedBookingFrom);
                const selectedBookingToDate = new Date(selectedBookingTo);

                // validate DateTime of future booking
                if (closestBookingWithLocal && selectedBookingToDate > new Date(closestBookingWithLocal.booked_from)) {
                    const [availableDays, availableHours] = getDatesDifferenceFromDateRange(
                        selectedBookingFromDate,
                        closestBookingWithLocal.booked_from,
                    );

                    setDateRangeErrorMessage({
                        field: 'datetime',
                        message: `${t('available_booking_message')}: ${availableDays} ${t('day_short')} ${availableHours} ${t('hour_short')}`,
                    });
                    return;
                }

                // validate booking for minimum required time (MIN_BOOKING_REQUIRED_TIME)
                if (
                    mode === BookingDeviceModalMode.CREATE &&
                    !hasMinimumRequiredBookingDuration(new Date(), selectedBookingToDate)
                ) {
                    setDateRangeErrorMessage({
                        field: 'datetime',
                        message: t('remaining_time_is_not_enough__message'),
                    });
                    return;
                }

                // validate booking for required period to contain
                if (bookingRequiredPeriod) {
                    const { leftMatches, rightMatches } = containsRange(
                        [selectedBookingFromDate, selectedBookingToDate],
                        bookingRequiredPeriod,
                    );
                    let message = '';

                    if (!leftMatches && !rightMatches) {
                        message = `${t('booking_must_start_at')} ${dateFormatting(bookingRequiredPeriod[0])} ${t('and')} ${t('finish_at')} ${dateFormatting(bookingRequiredPeriod[1])}`;
                    } else if (!leftMatches) {
                        message = `${t('booking_must_start_at')} ${dateFormatting(bookingRequiredPeriod[0])}`;
                    } else if (!rightMatches) {
                        message = `${t('booking_must_finish_at')} ${dateFormatting(bookingRequiredPeriod[1])}`;
                    }

                    if (message) {
                        setDateRangeErrorMessage({
                            field: 'datetime',
                            message: message,
                        });
                        return;
                    }
                }

                if (minimumHourDuration) {
                    if (!hasMinimumHourDuration(selectedBookingFromDate, selectedBookingToDate, minimumHourDuration)) {
                        const days = Math.floor(minimumHourDuration / 24);
                        const remainingHours = minimumHourDuration % 24;

                        setDateRangeErrorMessage({
                            field: 'datetime',
                            message: `${t('minimum_booking_period')} ${t('days', { count: days })} ${t('hours', { count: remainingHours })}`,
                        });
                        return;
                    }
                }

                // validate slots length of future booking
                if (slotIndex && futureBookingFrom && futureBookingTo) {
                    const futureBookingSlots = getFutureBookingSlots();

                    const neighbourBookingFirstSlotIndex = getNeighborBookingFirstSlotIndex(
                        {
                            booked_from: selectedBookingFromDate,
                            booked_to: selectedBookingToDate,
                            slots: futureBookingSlots,
                            pk: bookingForItem?.pk,
                        },
                        bookings,
                    );

                    if (neighbourBookingFirstSlotIndex) {
                        setDateRangeErrorMessage({
                            field: 'slots_count',
                            message: `${t('available_slots_message')} ${t('slots', { count: neighbourBookingFirstSlotIndex - slotIndex })}`,
                        });
                        return;
                    }
                }

                setDateRangeErrorMessage({ field: null, message: '' });
            }

            makeValidation();
            makeSelection();
        },
        [
            slotIndex,
            setBookingsSelection,
            bookingStartDate,
            closestBookingWithLocal,
            mode,
            bookingRequiredPeriod,
            minimumHourDuration,
            futureBookingFrom,
            futureBookingTo,
            setDateRangeErrorMessage,
            t,
            bookingForItem?.pk,
            bookings,
        ],
    );

    const handleDaysCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputEl = event.target;
        enforceMinMax(inputEl);
        const newDaysCountValue = Number(inputEl.value);
        let hoursCountLocal = hoursCount;
        setDaysCount(newDaysCountValue);

        // it is necessary to prevent 0d 0h range
        if (!newDaysCountValue && !hoursCount) {
            setHoursCount(MIN_HOURS_BOOKING);
            hoursCountLocal = MIN_HOURS_BOOKING;
        }

        makeSelectionAndValidation(newDaysCountValue, hoursCountLocal, slotsCount);
    };

    const handleHoursCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputEl = event.target;
        enforceMinMax(inputEl);
        const newHoursCountValue = Number(inputEl.value);
        setHoursCount(newHoursCountValue);
        makeSelectionAndValidation(daysCount, newHoursCountValue, slotsCount);
    };

    const handleSlotsCountChange = (event: SelectChangeEvent) => {
        const newSlotsCountValue = Number(event.target.value);
        setSlotsCount(newSlotsCountValue);
        makeSelectionAndValidation(daysCount, hoursCount, newSlotsCountValue);
    };

    useEffect(() => {
        if (hoursCount) {
            setIsInitialized(true);
        }
    }, [hoursCount]);

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        if (mode === BookingDeviceModalMode.CREATE || mode === BookingDeviceModalMode.MANAGE) {
            makeSelectionAndValidation(daysCount, hoursCount, slotsCount);
        }
    }, [isInitialized, daysCount, hoursCount, makeSelectionAndValidation, slotsCount, mode]);

    return (
        <>
            {selectedDevice && slotIndex !== undefined && (
                <Box sx={{ width: 217 }}>
                    <FormControl
                        disabled={isBookingInPast}
                        fullWidth
                        size="small"
                        margin="dense"
                        error={dateRangeErrorMessage.field === 'slots_count'}
                    >
                        <InputLabel id="slots-count-select-label">
                            {`${toTitleCase(selectedDevice.type)} ${t('slots')}`}
                        </InputLabel>
                        <Select
                            labelId="slots-count-select-label"
                            value={String(slotsCount)}
                            label={`${toTitleCase(selectedDevice.type)} ${t('slots')}`}
                            onChange={handleSlotsCountChange}
                        >
                            {generateSlotsIndexes(selectedDevice.slots_count - slotIndex + 1).map((item: SlotIndex) => (
                                <MenuItem key={item} value={item}>
                                    {`${t('slots', { count: item })} (${t('ports', {
                                        count:
                                            item *
                                            (selectedDevice.ports_count ||
                                                MAP_DEVICE_TYPE_TO_PORTS_PER_SLOT_COUNT[selectedDevice.type] ||
                                                0),
                                    })})`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}
            <Typography sx={{ fontSize: '1rem' }}>{t('duration')}</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Tooltip title={isBookingInPast && t('modify_past_booking_message')}>
                    <TextField
                        error={dateRangeErrorMessage.field === 'datetime'}
                        disabled={isBookingInPast}
                        sx={{ width: '100px', mt: 2 }}
                        value={daysCount}
                        onChange={handleDaysCountChange}
                        size="small"
                        type="number"
                        name="days_count"
                        label={t('input_days')}
                        inputProps={{
                            min: MIN_DAYS_BOOKING,
                            max: MAX_DAYS_BOOKING,
                        }}
                    />
                </Tooltip>
                <Tooltip title={isBookingInPast && t('modify_past_booking_message')}>
                    <TextField
                        autoFocus
                        error={dateRangeErrorMessage.field === 'datetime'}
                        disabled={isBookingInPast}
                        sx={{ width: '100px', mt: 2 }}
                        value={hoursCount}
                        onChange={handleHoursCountChange}
                        size="small"
                        type="number"
                        name="hours_count"
                        label={t('input_hours')}
                        inputProps={{
                            min: daysCount ? 0 : MIN_HOURS_BOOKING,
                            max: 23,
                        }}
                    />
                </Tooltip>
            </Box>
            {dateRangeErrorMessage.field && (
                <Alert sx={{ mt: 2, whiteSpace: 'pre-line' }} severity="error">
                    {dateRangeErrorMessage.message}
                </Alert>
            )}
        </>
    );
};

export default BookingDeviceModalForm;
